import { Component, OnInit } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RouterOutlet } from '@angular/router';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexSidebarComponent } from '@vex/components/vex-sidebar/vex-sidebar.component';

import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { SidenavComponent } from '../components/sidenav/sidenav.component';
import { ToolbarComponent } from '../components/toolbar/toolbar.component';
import { FooterComponent } from '../components/footer/footer.component';
import { ConfigPanelToggleComponent } from '../components/config-panel/config-panel-toggle/config-panel-toggle.component';
import { ConfigPanelComponent } from '../components/config-panel/config-panel.component';
import { MatDialogModule } from '@angular/material/dialog';
import { BaseLayoutComponent } from '../base-layout/base-layout.component';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { VexProgressBarComponent } from '@vex/components/vex-progress-bar/vex-progress-bar.component';
import { VexConfig } from '@vex/config/vex-config.interface';
import { WebPushComponent } from '../../common/components/web-push/web-push.component';
import { BadgeCountComponent } from '../../common/components';
import { ChatSubscriptionService } from '../../pages/chat/services/chat-subscription.service';
import { CurrentUserModel } from '@shared/common/models/current-user.model';
import { Store } from '@ngxs/store';
import { LoadChannelReads } from '../../pages/chat/store/actions/channel-read.actions';
import { LoadGlobalChannelStats } from '../../pages/chat/store/actions/channel.actions';
import { getAllowedChannelTypes, SERVICE_CATEGORIES } from '../../pages/chat/utils/chat-permissions.utils';
@Component({
    selector: 'vex-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    imports: [
        BaseLayoutComponent,
        NgIf,
        AsyncPipe,
        SidenavComponent,
        ToolbarComponent,
        FooterComponent,
        ConfigPanelToggleComponent,
        VexSidebarComponent,
        ConfigPanelComponent,
        MatDialogModule,
        MatSidenavModule,
        NgTemplateOutlet,
        RouterOutlet,
        VexProgressBarComponent,
        WebPushComponent,
        BadgeCountComponent,
    ],
    standalone: true,
})
export class LayoutComponent implements OnInit {
    config$: Observable<VexConfig> = this.configService.config$;
    sidenavCollapsed$: Observable<boolean> = this.layoutService.sidenavCollapsed$;
    sidenavDisableClose$: Observable<boolean> = this.layoutService.isDesktop$;
    sidenavFixedInViewport$: Observable<boolean> = this.layoutService.isDesktop$.pipe(
        map((isDesktop) => !isDesktop),
    );
    sidenavMode$: Observable<MatDrawerMode> = combineLatest([
        this.layoutService.isDesktop$,
        this.configService.select((config) => config.layout),
    ]).pipe(map(([isDesktop, layout]) => (!isDesktop || layout === 'vertical' ? 'over' : 'side')));
    sidenavOpen$: Observable<boolean> = this.layoutService.sidenavOpen$;
    configPanelOpen$: Observable<boolean> = this.layoutService.configPanelOpen$;
    quickpanelOpen$: Observable<boolean> = this.layoutService.quickpanelOpen$;

    constructor(
        private readonly layoutService: VexLayoutService,
        private readonly configService: VexConfigService,
        private readonly chatSubscriptionService: ChatSubscriptionService,
        private readonly currentUser: CurrentUserModel,
        private readonly store: Store,
    ) {}

    ngOnInit(): void {
        if (this.currentUser?.cognitoId) {
            const allowedTypes = getAllowedChannelTypes(this.currentUser.permissions, SERVICE_CATEGORIES);
            this.chatSubscriptionService.setAllowedTypes();
            this.chatSubscriptionService.startSubscriptions(this.currentUser.cognitoId);
            this.store.dispatch(new LoadChannelReads(this.currentUser.cognitoId, 500));
            this.store.dispatch(new LoadGlobalChannelStats(allowedTypes));
        }
    }

    onSidenavClosed(): void {
        this.layoutService.closeSidenav();
    }

    onQuickpanelClosed(): void {
        this.layoutService.closeQuickpanel();
    }
}
